"use client";
import React, { useState, useContext } from "react";
import { motion } from "framer-motion";
import {
  BanknotesIcon,
  QrCodeIcon,
  LockClosedIcon,
  ShieldCheckIcon,
} from "@heroicons/react/24/outline";
import { SiGooglepay, SiPhonepe, SiAmazonpay, SiPaytm } from "react-icons/si";
import Enquiry from "./Enquiry";
import { ThemeContext } from "../context/ThemeContext";

const Payment = () => {
  const [bankCopyStatus, setBankCopyStatus] = useState("");
  const [upiCopyStatus, setUpiCopyStatus] = useState("");
  const { theme } = useContext(ThemeContext);
  const isDark = theme === "dark";

  const paymentMethods = [
    { name: "Google Pay", icon: SiGooglepay, color: "text-[#5F6368]" },
    { name: "PhonePe", icon: SiPhonepe, color: "text-[#5F259F]" },
    { name: "Amazon Pay", icon: SiAmazonpay, color: "text-[#FF9900]" },
    { name: "Paytm", icon: SiPaytm, color: "text-[#00BAF2]" },
  ];

  const handleCopy = (text, type) => {
    navigator.clipboard.writeText(text);
    const msg = "Copied to clipboard!";
    if (type === "bank") {
      setBankCopyStatus(msg);
      setTimeout(() => setBankCopyStatus(""), 2000);
    } else {
      setUpiCopyStatus(msg);
      setTimeout(() => setUpiCopyStatus(""), 2000);
    }
  };

  const qrImages = [
    { src: "/qr.jpg", name: "Tripocation-UPI-QR" },
    { src: "/qrcode.png", name: "Alternate-UPI-QR" },
  ];

  const upiIds = ["9220198202@okbizaxis", "tripocationholiday@sbi"];

  return (
    <section
      className={`${
        isDark ? "dark" : ""
      } bg-gradient-to-br from-gray-50 to-blue-50 dark:from-gray-900 dark:to-gray-800 min-h-screen pt-40 pb-16 px-4 sm:px-6 lg:px-12`}
    >
      <div className="max-w-7xl mx-auto text-gray-900 dark:text-gray-100">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-12"
        >
          <h1 className="text-3xl sm:text-4xl font-bold mb-3">
            Secure Payment Gateway
          </h1>
          <div className="flex items-center justify-center gap-2 text-blue-600 dark:text-blue-400">
            <LockClosedIcon className="h-6 w-6" />
            <p className="font-medium text-sm sm:text-base">
              256-bit SSL Encrypted Transactions
            </p>
          </div>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-start">
          {/* Left Panel */}
          <motion.div initial="hidden" animate="visible" className="space-y-8">
            {/* Bank Transfer */}
            <motion.div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg p-6 sm:p-8 border border-gray-100 dark:border-gray-700">
              <div className="flex items-center gap-4 mb-6">
                <BanknotesIcon className="h-8 w-8 text-[#007bff]" />
                <h2 className="text-xl sm:text-2xl font-bold">
                  Bank Transfer Details
                </h2>
              </div>
              <div className="space-y-4 text-sm">
                {[
                  { label: "Bank Name", value: "State Bank of India" },
                  {
                    label: "Account Holder",
                    value: "Tripocation Holiday Private Limited",
                  },
                  { label: "Account Number", value: "44052564185" },
                  { label: "IFSC Code", value: "SBIN0004394" },
                  { label: "Branch", value: "ROHINI, NEW DELHI - 110085" },
                ].map((detail, index) => (
                  <motion.div
                    key={index}
                    whileHover={{ x: 5 }}
                    className="p-3 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 cursor-pointer"
                    onClick={() => handleCopy(detail.value, "bank")}
                    aria-label={`Copy ${detail.label}`}
                  >
                    <div className="flex flex-col sm:flex-row sm:justify-between">
                      <span className="font-medium">{detail.label}:</span>
                      <span className="font-mono">{detail.value}</span>
                    </div>
                  </motion.div>
                ))}
              </div>
              {bankCopyStatus && (
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  className="mt-4 text-sm text-blue-600 flex items-center gap-2"
                >
                  <ShieldCheckIcon className="h-4 w-4" />
                  {bankCopyStatus}
                </motion.div>
              )}
            </motion.div>

            {/* Instant Payments */}
            <motion.div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg p-6 sm:p-8 border border-gray-100 dark:border-gray-700">
              <h3 className="text-lg font-semibold mb-4">
                Instant Payment Options
              </h3>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
                {paymentMethods.map(({ name, icon: Icon, color }, index) => (
                  <motion.div
                    key={index}
                    whileHover={{ y: -5 }}
                    className="p-4 rounded-lg border border-gray-200 dark:border-gray-700 flex items-center justify-center aspect-square bg-white dark:bg-gray-900"
                    title={name}
                    aria-label={name}
                  >
                    <Icon className={`h-10 w-10 ${color}`} />
                  </motion.div>
                ))}
              </div>
            </motion.div>
          </motion.div>

          {/* Right Panel */}
          <motion.div
            initial={{ opacity: 0, x: 50 }}
            animate={{ opacity: 1, x: 0 }}
            className="sticky top-8 bg-white dark:bg-gray-800 rounded-xl shadow-lg border border-gray-100 dark:border-gray-700"
          >
            <div className="p-6 sm:p-8">
              <div className="flex items-center gap-4 mb-6">
                <QrCodeIcon className="h-8 w-8 text-[#007bff]" />
                <h2 className="text-xl sm:text-2xl font-bold">Scan to Pay</h2>
              </div>

              {/* QR Images */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 text-center">
                {qrImages.map(({ src, name }, idx) => (
                  <div
                    key={idx}
                    className="bg-gray-50 dark:bg-gray-900 p-4 rounded-lg shadow-md"
                  >
                    <img
                      src={src}
                      alt={`${name} QR Code`}
                      className="h-52 w-52 sm:h-60 sm:w-60 object-contain mx-auto rounded-md"
                    />
                    <button
                      onClick={() => {
                        const link = document.createElement("a");
                        link.href = src;
                        link.download = `${name}.png`;
                        link.click();
                      }}
                      className="mt-3 text-sm text-blue-500 underline"
                    >
                      Download QR Code
                    </button>
                  </div>
                ))}
              </div>

              {/* UPI IDs */}
              <div className="mt-6 space-y-3">
                {upiIds.map((upi, idx) => (
                  <div
                    key={idx}
                    onClick={() => handleCopy(upi, "upi")}
                    className="p-3 bg-blue-50 dark:bg-blue-900 rounded-md cursor-pointer hover:bg-blue-100 dark:hover:bg-blue-800 transition"
                    aria-label={`Copy ${upi}`}
                  >
                    <p className="font-mono text-sm text-blue-600 dark:text-blue-300">
                      UPI ID: <span className="font-semibold">{upi}</span>
                    </p>
                    <span className="text-xs text-gray-500">(Tap to copy)</span>
                  </div>
                ))}
              </div>

              {upiCopyStatus && (
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  className="mt-2 text-sm text-blue-600 flex items-center justify-center gap-2"
                >
                  <ShieldCheckIcon className="h-4 w-4" />
                  {upiCopyStatus}
                </motion.div>
              )}
            </div>

            {/* Contact Info */}
            <div className="bg-gray-50 dark:bg-gray-900 p-6 border-t border-gray-200 dark:border-gray-700">
              <div className="text-sm text-gray-600 dark:text-gray-300 space-y-2">
                <p>
                  Need help? Call{" "}
                  <span className="text-blue-600 dark:text-blue-400 cursor-pointer">
                    +91 92201 98202
                  </span>
                </p>
                <p>
                  or Email{" "}
                  <span className="text-blue-600 dark:text-blue-400 cursor-pointer">
                    prince@tripocationholiday.com
                  </span>
                </p>
              </div>
            </div>
          </motion.div>
        </div>
      </div>

      <Enquiry />
    </section>
  );
};

export default Payment;
